import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Toggle, Stack, Label, Icon } from "@fluentui/react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  Checkbox,
  Chip,
  Tooltip,
  IconButton as MuiIconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { useTheme } from "../../theme/OAS365Theme";
import { useViewCopyStyle } from "../../theme/styles/ViewCopyStyle";
import { useUploadFilesItem } from "../../theme/styles/UploadFilesItemStyle";
import { useDragAndDropStyle } from "../../theme/styles/DragAndDropStyle";
import { useLogin } from "../../redux/hooks/useLogin";
import { useOneDrive } from "../../redux/hooks/useOneDrive";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useCheckinDocument } from "../../redux/hooks/useCheckinDocument";
import { getDocumentDetails } from "../../redux/api/documentsAPI";
import { normalizeDocumentRevisionInfoResponse } from "../../utils/normalizeDocumentRevisionInfoResponse";
import { resolveGroupId } from "../check-in-new-list/checkInNewList.utils";
import { parseBulkClassRules } from "./checkInBulk.utils";
import {
  buildCheckoutRevision,
  buildExistingCheckinBody,
  getBase64FromUrl,
  isWordLikeExtension,
} from "./checkedOutBulk.utils";
import {
  getFileExtension,
  isValidPDFFileType,
} from "../../functions/helperMethod";
import {
  AccordionSummaryWithValidation,
  getAccordionSummarySx,
} from "../../components/checkinDocuments/AccordionSummaryWithValidation";
import { CHECKIN_VALIDATION_VIEW_COPY_REQUIRED } from "../../redux/constants/messageTypes";
import UserAuth from "../../auth/userAuth";
import CustomerConfig from "../../azure/CustomerConfig";
import {
  DOC_CHECK_IN_TYPE,
  FILE_AUTO_ENABLE,
  FILE_AUTO_ENABLE_SUB,
  FILE_DRAG_AND_DROP_PDF,
  FILE_DRAG_AND_DROP_TEXT,
  FILE_DRAG_AND_DROP_SIZE,
  VIEW_COPY_TYPE,
} from "../../redux/constants/metaData";
import {
  RELEASE_DOCUMENT,
  RELEASE_DOCUMENT_TOOLTIP,
  DELETE_ONEDRIVE_DOC,
  DELETE_ONEDRIVE_DOC_TOOLTIP,
  ONEDRIVE_DOCUMENT_DELETED,
} from "../../redux/constants/labelText";
import { logger } from "../../utils/logger";
import DragAndDrop from "../../assets/icons/dragAndDrop.svg";
import docChecked from "../../assets/workingDocIcons/docChecked.svg";
import docDelete from "../../assets/workingDocIcons/docDelete.svg";
import "../../css/FileDragDrop.css";
import "../../theme/css/viewCopyDragDropZone.css";

const CARD_SURFACE_BG = "#f4fbf6";
const MAX_VIEW_COPY_BYTES = 1024 * 1024 * 20.5;

const HEADER_SUMMARY_SX = {
  minHeight: "56px !important",
  "&.Mui-expanded": { minHeight: "56px !important" },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: "12px 0 !important",
  },
};

const isApiFlagYes = (value) => value === true || value === "Y";

/** Truncate long file names the same way the new-document ViewCopyRecord does. */
const formatViewCopyFileName = (name = "") => {
  if (name.length > 25) {
    const ext = name.split(".").pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf("."));
    return `${nameWithoutExt.substring(0, 20)}...${ext ? `.${ext}` : ""}`;
  }
  return name;
};

/**
 * A checked-out card's mandatory view copy is satisfied when the class does not
 * require one, or when auto-generate is on (Word docs) or a PDF is uploaded.
 */
const isViewCopySatisfied = (draft) => {
  if (!isApiFlagYes(draft?.viewRequired)) return true;
  if (draft?.wordLike && draft?.createViewCopy === true) return true;
  return !!draft?.viewCopyFile;
};

/** Card is ready to submit: loaded, has a revision, and view copy satisfied. */
const isCardComplete = (draft) =>
  !!draft &&
  draft.loading !== true &&
  !!draft.documentRevision &&
  isViewCopySatisfied(draft);

/** Self-contained PDF view-copy dropzone (per card, no shared Redux). */
function BulkViewCopyDropzone({ isDisabled, onFileChange, boxHeight = "180px" }) {
  const theme = useTheme();
  const dragStyle = useDragAndDropStyle();
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState("");

  const acceptFile = useCallback(
    (file) => {
      if (!file) return;
      const ext = getFileExtension(file.name);
      if (!ext || !isValidPDFFileType(ext, VIEW_COPY_TYPE)) {
        setMessage(
          ext
            ? `The ${ext.toUpperCase()} file type is not supported for View Copy. Only PDF files are allowed.`
            : "Invalid file type. Only PDF files are allowed.",
        );
        return;
      }
      if (file.size > MAX_VIEW_COPY_BYTES) {
        setMessage("File upload failed. The maximum file you can upload is 20 MB.");
        return;
      }
      const reader = new FileReader();
      reader.onerror = () =>
        setMessage("Failed to read the selected file. Please try again.");
      reader.onload = () => {
        const result = reader.result;
        const sizeKb = Math.max(1, Math.round(file.size / 1024));
        onFileChange({
          name: file.name,
          base64str: result.substring(result.lastIndexOf(",") + 1),
          size: `${sizeKb} KB`,
        });
        setMessage("");
      };
      reader.readAsDataURL(file);
    },
    [onFileChange],
  );

  const handleDrag = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };
  const handleDrop = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      acceptFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <Stack className="container" style={{ width: "100%" }}>
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        id="div-file-upload"
        onDragEnter={handleDrag}
        style={{ height: boxHeight }}
      >
        <input
          ref={inputRef}
          type="file"
          id="input-file-upload"
          accept="application/pdf,.pdf"
          disabled={isDisabled}
          onChange={(e) => {
            if (isDisabled) return;
            if (e.target.files && e.target.files[0]) acceptFile(e.target.files[0]);
            e.target.value = null;
          }}
        />
        <label
          style={{
            width: "100%",
            height: boxHeight,
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.6 : 1,
          }}
          id="label-file-upload"
          className={dragActive && !isDisabled ? "drag-active" : ""}
        >
          <Stack>
            <Stack.Item>
              <button
                type="button"
                className="upload-button"
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isDisabled) inputRef.current?.click();
                }}
                style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
              >
                <Stack.Item>
                  <img
                    style={{
                      marginBottom: "8px",
                      verticalAlign: "middle",
                      filter: isDisabled ? "grayscale(100%)" : "none",
                    }}
                    src={DragAndDrop}
                    alt="drag and drop"
                  />
                </Stack.Item>
                <Stack.Item style={dragStyle.uploadTextSpan}>
                  <span
                    style={{
                      ...dragStyle.uploadTextHighlighted,
                      color: isDisabled
                        ? theme.colors.viewCopyUploadTextColor
                        : dragStyle.uploadTextHighlighted.color,
                      fontWeight: "normal",
                    }}
                  >
                    {FILE_DRAG_AND_DROP_PDF}
                  </span>
                  <span
                    style={{
                      color: isDisabled
                        ? theme.colors.viewCopyUploadTextColor
                        : "inherit",
                      fontWeight: "normal",
                    }}
                  >
                    {FILE_DRAG_AND_DROP_TEXT}
                  </span>
                  <br />
                  <span
                    style={{
                      color: isDisabled
                        ? theme.colors.viewCopyUploadTextColor
                        : "inherit",
                      fontWeight: "normal",
                    }}
                  >
                    {FILE_DRAG_AND_DROP_SIZE}
                  </span>
                </Stack.Item>
              </button>
            </Stack.Item>
          </Stack>
        </label>
        {dragActive && !isDisabled && (
          <div
            id="drag-file-element"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
        )}
        <Label className="labelStyles" style={dragStyle.validationStyle}>
          {message}
        </Label>
      </Stack>
    </Stack>
  );
}

/** View Copy accordion: auto-generate toggle (Word) + PDF upload, per card. */
function BulkViewCopyBlock({
  wordLike,
  viewRequired,
  createViewCopy,
  onToggle,
  viewCopyFile,
  onFileChange,
  onFileRemove,
  theme,
}) {
  const styles = useViewCopyStyle();
  const fileStyleTheme = useUploadFilesItem();
  const autoGenOn = wordLike && createViewCopy === true;
  const hasFile = !!viewCopyFile;
  // Match the OneDrive bulk check-in (DocumentBasicItem) box sizing exactly.
  const leftBoxHeight = "147px";
  const rightBoxHeight = "180px";
  const uploadBoxHeight = hasFile ? "110px" : rightBoxHeight;

  // Validation: a view-copy-required class needs either auto-generate (Word) or
  // an uploaded PDF, otherwise the field is invalid and check-in is blocked.
  const viewCopyError =
    isApiFlagYes(viewRequired) &&
    !isViewCopySatisfied({ viewRequired, wordLike, createViewCopy, viewCopyFile })
      ? CHECKIN_VALIDATION_VIEW_COPY_REQUIRED
      : "";

  // Open the accordion when a view copy is mandatory or currently invalid.
  // `viewRequired` resolves asynchronously (starts "N", may become "Y" after
  // the class rules load), so sync the expanded state to it while still
  // allowing the user to toggle it manually afterwards.
  const [expanded, setExpanded] = useState(isApiFlagYes(viewRequired));
  useEffect(() => {
    setExpanded(isApiFlagYes(viewRequired));
  }, [viewRequired]);

  return (
    <Accordion
      style={styles.accordianStyle}
      disableGutters
      expanded={expanded || !!viewCopyError}
      onChange={(_e, isExpanded) => setExpanded(isExpanded)}
      sx={{ "&:before": { display: "none" } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
        aria-controls="bulk-view-copy-content"
        id="bulk-view-copy-header"
        sx={getAccordionSummarySx(!!viewCopyError)}
      >
        <AccordionSummaryWithValidation
          title="View Copy"
          error={viewCopyError}
          titleStyle={styles.accordianTextStyle}
        />
      </AccordionSummary>
      <AccordionDetails style={styles.accordianDetails}>
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
            alignItems: "stretch",
            flexDirection: "row",
          }}
        >
          {wordLike && (
            <Box
              style={styles.accordianBoxStyle}
              sx={{ height: leftBoxHeight }}
            >
              <Box>
                <Toggle
                  style={{ fontFamily: theme.fonts.regular }}
                  checked={createViewCopy === true}
                  onChange={(_e, checked) => onToggle(checked === true)}
                />
              </Box>
              <Typography style={styles.viewCopyTextStyle}>
                {FILE_AUTO_ENABLE}
                <br />
                {FILE_AUTO_ENABLE_SUB}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              width: "100%",
              height: rightBoxHeight,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Stack disableShrink style={{ height: "100%" }}>
                <Stack.Item
                  styles={fileStyleTheme.ViewCopyStackStyle}
                  sx={{
                    flex: hasFile ? 0.6 : 1,
                    transition: "flex 0.3s ease",
                    minHeight: uploadBoxHeight,
                  }}
                >
                  <BulkViewCopyDropzone
                    isDisabled={autoGenOn}
                    onFileChange={onFileChange}
                    boxHeight={uploadBoxHeight}
                  />
                </Stack.Item>
                {hasFile && (
                  <Stack.Item sx={{ flex: 0.4, transition: "flex 0.3s ease" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                        borderRadius: "4px",
                        padding: "8px 12px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <InsertDriveFileIcon
                          sx={{ color: "#4c8d6a", fontSize: 20, flexShrink: 0 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            title={viewCopyFile.name}
                            sx={{
                              fontFamily: theme.fonts.regular,
                              fontWeight: 500,
                              color: "#212529",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {formatViewCopyFileName(viewCopyFile.name)}
                          </Typography>
                          {viewCopyFile.size && (
                            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                              <Chip
                                label={viewCopyFile.size}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "10px",
                                  backgroundColor: "#e8f5e9",
                                  color: "#2e7d32",
                                  fontFamily: theme.fonts.regular,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <MuiIconButton
                        size="small"
                        onClick={onFileRemove}
                        sx={{
                          color: "#dc3545",
                          "&:hover": {
                            backgroundColor: "rgba(220, 53, 69, 0.1)",
                          },
                          flexShrink: 0,
                          marginLeft: 1,
                        }}
                      >
                        <CancelIcon fontSize="small" />
                      </MuiIconButton>
                    </Box>
                  </Stack.Item>
                )}
              </Stack>
            </Box>
          </Box>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

const CheckedOutBulkSection = forwardRef(function CheckedOutBulkSection(
  { documents, onReadyChange },
  ref,
) {
  const theme = useTheme();
  const { teamsContext } = useLogin();
  const { fetchOneDriveDownloadUrl, deleteOneDriveDocumentsBulk } = useOneDrive();
  const { onGetFormatsByClass } = useCheckin();
  const { checkinExistingDocumentsBulk } = useCheckinDocument();

  const deleteForced = useMemo(
    () => new CustomerConfig().getIsOneDriveDocDeleteEnable() === true,
    [],
  );

  /** @type {[Record<string, any>, Function]} */
  const [draftByKey, setDraftByKey] = useState({});
  const draftByKeyRef = useRef(draftByKey);
  useEffect(() => {
    draftByKeyRef.current = draftByKey;
  }, [draftByKey]);

  /** Docs the user removed from the checked-out list via the header X. */
  const [removedKeys, setRemovedKeys] = useState(() => new Set());
  const docList = useMemo(() => {
    const all = Array.isArray(documents) ? documents : [];
    return all.filter((d) => !removedKeys.has(String(d.key)));
  }, [documents, removedKeys]);

  const handleRemoveDoc = useCallback((key) => {
    const id = String(key);
    setRemovedKeys((prev) => new Set(prev).add(id));
    setDraftByKey((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // Seed a draft per document and auto-populate revision + view-copy rules.
  useEffect(() => {
    if (docList.length === 0) return undefined;
    let cancelled = false;

    setDraftByKey((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const doc of docList) {
        const id = String(doc.key);
        if (!next[id]) {
          next[id] = {
            loading: true,
            wordLike: isWordLikeExtension(doc?.fileExtension),
            documentRevision: null,
            viewRequired: "N",
            createViewCopy: false,
            viewCopyFile: null,
            docReleaseStatus: "",
            oneDriveDeleteRequired: deleteForced,
            // Card starts collapsed; opens after load only when view copy is required.
            cardExpanded: false,
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // Load each document's details concurrently (the OneDrive bulk flow does
    // the same). A sequential loop serialized every doc's details + retry, which
    // made the section slow to populate for multi-doc selections.
    const loadOne = async (doc, accessToken) => {
      const id = String(doc.key);
      if (draftByKeyRef.current[id]?.documentRevision) return;
      try {
        const { data, status } = await getDocumentDetails(
          {
            DocClass: doc.docClass,
            DocNo: doc.docNo,
            DocSheet: doc.docSheet,
            DocRev: doc.docRev,
          },
          accessToken,
        );
        if (cancelled) return;
        const revision =
          status === 200
            ? normalizeDocumentRevisionInfoResponse(data)?.revision ?? {}
            : {};
        const documentRevision = buildCheckoutRevision(revision, doc);

        // Resolve the class' view-copy rule. The first get-docformatinfo call
        // can race and return no formats, so retry (same as the OneDrive bulk
        // autopopulation) before deciding view copy is not required.
        const classForRules = doc.docClass || documentRevision.docClass || "";
        let viewRequired = "N";
        try {
          for (let attempt = 0; attempt < 3; attempt += 1) {
            const payload = await onGetFormatsByClass(classForRules);
            const rules = parseBulkClassRules(payload);
            viewRequired = rules?.viewRequired ?? "N";
            if ((rules?.formats?.length ?? 0) > 0 || isApiFlagYes(viewRequired)) {
              break;
            }
            if (cancelled) return;
            if (attempt < 2) {
              await new Promise((resolve) =>
                setTimeout(resolve, 400 * (attempt + 1)),
              );
            }
          }
        } catch (err) {
          logger.warn(
            "CheckedOutBulkSection: formats-by-class failed",
            err,
            "CHECKIN",
          );
        }
        if (cancelled) return;

        const wordLike = isWordLikeExtension(documentRevision.FileExtension);
        setDraftByKey((prev) => {
          const row = prev[id];
          if (!row) return prev;
          return {
            ...prev,
            [id]: {
              ...row,
              loading: false,
              wordLike,
              documentRevision,
              viewRequired,
              createViewCopy: isApiFlagYes(viewRequired) && wordLike,
              cardExpanded: isApiFlagYes(viewRequired),
            },
          };
        });
      } catch (err) {
        logger.warn("CheckedOutBulkSection: detail load failed", err, "CHECKIN");
        if (cancelled) return;
        setDraftByKey((prev) => {
          const row = prev[id];
          if (!row) return prev;
          return {
            ...prev,
            [id]: {
              ...row,
              loading: false,
              documentRevision: buildCheckoutRevision({}, doc),
            },
          };
        });
      }
    };

    (async () => {
      const tokenResponse = await new UserAuth().getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (cancelled || !accessToken) return;
      await Promise.all(docList.map((doc) => loadOne(doc, accessToken)));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docList]);

  const allReady = useMemo(() => {
    if (docList.length === 0) return true;
    return docList.every((doc) => isCardComplete(draftByKey[String(doc.key)]));
  }, [docList, draftByKey]);

  useEffect(() => {
    onReadyChange?.(allReady);
  }, [allReady, onReadyChange]);

  const setDraftField = useCallback((id, patch) => {
    setDraftByKey((prev) => {
      const row = prev[id];
      if (!row) return prev;
      return { ...prev, [id]: { ...row, ...patch } };
    });
  }, []);

  /** Resolve the OneDrive download URL for a checked-out doc. */
  const resolveDownloadUrl = useCallback(
    async (doc) => {
      const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
      const result = await fetchOneDriveDownloadUrl(
        groupId ?? "",
        channelId ?? "",
        teamsContext?.user?.tenant?.id ?? "",
        String(doc.key),
        fileRoot,
        DOC_CHECK_IN_TYPE.checkinNew,
      );
      return result?.meta?.requestStatus === "fulfilled" &&
        typeof result?.payload === "string" &&
        result.payload.trim() !== ""
        ? result.payload
        : null;
    },
    [fetchOneDriveDownloadUrl, teamsContext],
  );

  /**
   * Sequentially check in every checked-out document (plus any manual view
   * copy), then delete the OneDrive originals still flagged. Returns an
   * aggregate the parent folds into its toast.
   */
  const submit = useCallback(async () => {
    const drafts = draftByKeyRef.current;
    const bodies = [];

    for (const doc of docList) {
      const id = String(doc.key);
      const draft = drafts[id];
      if (!draft?.documentRevision) continue;
      try {
        const url = await resolveDownloadUrl(doc);
        if (!url) throw new Error("no download url");
        const fileData = await getBase64FromUrl(url);
        const autoGenOn = draft.wordLike && draft.createViewCopy === true;
        bodies.push(
          buildExistingCheckinBody({
            documentRevision: draft.documentRevision,
            fileName: doc.name,
            fileData,
            createViewCopy: autoGenOn,
            docReleaseStatus: draft.docReleaseStatus,
            viewCopyFile: autoGenOn ? null : draft.viewCopyFile,
            viewRequired: isApiFlagYes(draft.viewRequired),
            meta: { id, oneDriveId: String(doc.key) },
          }),
        );
      } catch (err) {
        logger.warn(
          "CheckedOutBulkSection: prepare failed",
          { id, message: err?.message },
          "CHECKIN",
        );
      }
    }

    logger.info(
      "CheckedOutBulkSection: submit prepared",
      { total: docList.length, bodies: bodies.length },
      "CHECKIN",
    );

    let successCount = 0;
    let results = [];
    let rejectedMessage = "";
    if (bodies.length > 0) {
      const action = await checkinExistingDocumentsBulk({ documents: bodies });
      results = action?.payload?.data?.results ?? [];
      successCount = action?.payload?.data?.successCount ?? 0;
      // Thunk was rejected (e.g. token) rather than fulfilled with results.
      if (!action?.payload?.data) {
        rejectedMessage =
          action?.payload?.message ?? "Check-in request failed.";
      }
    }
    const firstError =
      rejectedMessage ||
      results.find((r) => Number(r.statusCode) !== 200)?.errorMessage ||
      (bodies.length === 0 ? "Could not prepare the document(s)." : "");

    const succeededIds = new Set(
      results
        .filter((r) => Number(r.statusCode) === 200)
        .map((r) => r.oneDriveId),
    );
    const deleteIds = docList
      .filter((doc) => {
        const draft = drafts[String(doc.key)];
        return (
          succeededIds.has(String(doc.key)) &&
          (deleteForced || draft?.oneDriveDeleteRequired === true)
        );
      })
      .map((doc) => String(doc.key));

    if (deleteIds.length > 0) {
      try {
        const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
        await deleteOneDriveDocumentsBulk(
          teamsContext?.channel?.membershipType || "Regular",
          groupId ?? "",
          channelId ?? "",
          teamsContext?.user?.tenant?.id ?? "",
          fileRoot,
          deleteIds,
        );
      } catch (_err) {
        // Deletion failure must not fail the check-in result.
      }
    }

    return {
      successCount,
      failureCount: docList.length - successCount,
      firstError,
    };
  }, [
    checkinExistingDocumentsBulk,
    deleteForced,
    deleteOneDriveDocumentsBulk,
    docList,
    resolveDownloadUrl,
    teamsContext,
  ]);

  useImperativeHandle(ref, () => ({ submit, isReady: () => allReady }), [
    submit,
    allReady,
  ]);

  if (docList.length === 0) return null;

  return (
    <Box sx={{ width: "100%", mb: "20px" }}>
      <Box
        sx={{
          mb: "12px",
          pb: "6px",
          borderBottom: `2px solid ${theme.colors.addovation20Green ?? "#b7d9c8"}`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontFamily: theme.fonts.regular,
            fontSize: `${(theme.fontSizes?.normal ?? 12) + 2}px`,
            color: theme.colors.themeDarkColor ?? "#237b4b",
          }}
        >
          Check-in Existing
        </Typography>
      </Box>

      <Box>
      {docList.map((doc) => {
        const id = String(doc.key);
        const draft = draftByKey[id] ?? {};
        const docLabel = doc.name ?? doc.title ?? "Document";
        const releaseChecked = draft.docReleaseStatus === "Released";
        const deleteChecked = deleteForced || draft.oneDriveDeleteRequired === true;
        // Warn (like the New Document File rows) whenever the card is not yet
        // ready to submit — while loading or with an unmet required field.
        const cardIncomplete = !isCardComplete(draft);
        return (
          <Accordion
            key={id}
            disableGutters
            expanded={draft.cardExpanded === true}
            onChange={(_e, isExpanded) =>
              setDraftField(id, { cardExpanded: isExpanded })
            }
            sx={{
              mb: 1.25,
              boxShadow: "none !important",
              borderRadius: "8px",
              border: `1px solid ${theme.colors.addovation20Green ?? "#b7d9c8"}`,
              "&:before": { display: "none" },
              backgroundColor: CARD_SURFACE_BG,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
              sx={{
                ...HEADER_SUMMARY_SX,
                pl: 1.5,
                pr: 2.5,
                "& .MuiAccordionSummary-content": {
                  ...HEADER_SUMMARY_SX["& .MuiAccordionSummary-content"],
                  gap: "12px",
                  minWidth: 0,
                  overflow: "hidden",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  marginLeft: "2px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <img
                src={doc.iconName}
                alt=""
                width={18}
                height={18}
                style={{ flexShrink: 0 }}
                onError={(e) => {
                  e.target.style.visibility = "hidden";
                }}
              />
              <Typography
                title={docLabel}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#2d6e4f",
                  fontFamily: theme.fonts.regular,
                  fontWeight: 600,
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                }}
              >
                {docLabel}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  flexShrink: 0,
                }}
              >
                {cardIncomplete && (
                  <Tooltip
                    title="Complete all required fields"
                    placement="top"
                    arrow
                  >
                    <span style={{ display: "inline-flex" }}>
                      <Icon
                        iconName="Warning"
                        ariaLabel="Complete all required fields"
                        styles={{
                          root: {
                            flexShrink: 0,
                            color: "#eab308",
                            fontSize: 18,
                            height: 18,
                            width: 18,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Remove document" arrow placement="top">
                  <MuiIconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveDoc(id);
                    }}
                    sx={{
                      padding: 0,
                      width: 22,
                      height: 22,
                      color: "#d32f2f",
                      flexShrink: 0,
                      "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 22 }} />
                  </MuiIconButton>
                </Tooltip>
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#bdbdbd",
                  fontSize: "12px",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                |
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                pt: "14px",
                pb: 2,
                px: 2,
                backgroundColor: theme.colors.backgroundColor,
              }}
            >
              <BulkViewCopyBlock
                wordLike={draft.wordLike === true}
                viewRequired={draft.viewRequired}
                createViewCopy={draft.createViewCopy === true}
                onToggle={(value) =>
                  setDraftField(id, {
                    createViewCopy: value,
                    // Auto-generate and manual upload are mutually exclusive.
                    ...(value ? { viewCopyFile: null } : {}),
                  })
                }
                viewCopyFile={draft.viewCopyFile}
                onFileChange={(file) =>
                  setDraftField(id, { viewCopyFile: file, createViewCopy: false })
                }
                onFileRemove={() => setDraftField(id, { viewCopyFile: null })}
                theme={theme}
              />

              {/* Release Document */}
              <Box
                sx={{
                  mt: "10px",
                  pl: "5px",
                  pr: "16px",
                  height: "43px",
                  backgroundColor: "#E6F4EA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={releaseChecked}
                    onChange={(e) =>
                      setDraftField(id, {
                        docReleaseStatus: e.target.checked ? "Released" : "",
                      })
                    }
                    sx={{ color: "#4c8d6a", "&.Mui-checked": { color: "#4c8d6a" } }}
                  />
                  <Tooltip title={RELEASE_DOCUMENT_TOOLTIP} placement="top" arrow>
                    <Typography
                      sx={{
                        color: "#4c8d6a",
                        fontWeight: "bold",
                        fontFamily: theme.fonts?.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                        cursor: "default",
                      }}
                    >
                      {RELEASE_DOCUMENT}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box
                  component="img"
                  src={docChecked}
                  alt="icon"
                  sx={{ width: 25, height: 25 }}
                />
              </Box>

              {/* OneDrive delete notice / checkbox */}
              <Box
                sx={{
                  mt: "10px",
                  pl: "5px",
                  pr: "16px",
                  height: "43px",
                  backgroundColor: "#F0EEE2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {!deleteForced && (
                    <Checkbox
                      checked={deleteChecked}
                      onChange={(e) =>
                        setDraftField(id, {
                          oneDriveDeleteRequired: !!e.target.checked,
                        })
                      }
                      sx={{
                        color: "#AE9B2E",
                        "&.Mui-checked": { color: "#AE9B2E" },
                      }}
                    />
                  )}
                  <Tooltip title={DELETE_ONEDRIVE_DOC_TOOLTIP} placement="top" arrow>
                    <Typography
                      sx={{
                        color: "#AE9B2E",
                        fontWeight: "bold",
                        fontFamily: theme.fonts?.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                        cursor: "default",
                      }}
                    >
                      {deleteForced ? (
                        <span style={{ marginLeft: 10 }}>
                          {ONEDRIVE_DOCUMENT_DELETED}
                        </span>
                      ) : (
                        DELETE_ONEDRIVE_DOC
                      )}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box
                  component="img"
                  src={docDelete}
                  alt="icon"
                  sx={{
                    width: 30,
                    height: 30,
                    opacity: deleteForced ? 0.5 : "initial",
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
      </Box>
    </Box>
  );
});

export default CheckedOutBulkSection;
